/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import config from '../../../../share/config';
import session from '../../../../share/session';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import util from '../../../../share/util/utils';
import error from '../../../../common/nodejs/errors';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  OPERATION_CONFIRMATION: 'operationConfirmation',
  RE_OPERATION_CONFIRMATION: 'reOperationConfirmation',
  RE_REGISTER_RESERVE: 'reRegisterReserve',
  RE_REGISTER: 'reRegister',
  SUCCESS: 'success',
  NEGATIVE_RESPONSE: 'negativeResponse',
  NON_RESPONSE: 'nonResponse',
  CONDITION_FAILED: 'conditionFailed'
};

export default {
  name: 'requirement101',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      systemId: '',
      displayInfo: '',
      modal_requirement_101: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.NEGATIVE_RESPONSE:
        case PHASE.NON_RESPONSE:
        case PHASE.CONDITION_FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_101 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_101 = false;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /**
     * データ読出し
     */
    async read() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        // データ読み出し(要求)
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              switch (response.data.user_data.value) {
                // IgOnStatusがFF(ON)の場合
                case "FF":
                  // LAN接続ユニット再登録準備画面を表示し、StandbyAPIを実行
                  this.phase = PHASE.RE_REGISTER_RESERVE;
                  await this.standby();
                  break;
                // IgOnStatusが00(OFF)の場合
                case "00":
                  // HACK: Phase4Step2-2終了間際にESLintのルールが変更になり、ロジックは変更出来なかったため回避。
                  /* eslint-disable max-depth */
                  if (this.phase === PHASE.OPERATION_CONFIRMATION) {
                    // IG ON操作表示から実行されている場合、IG ON再操作表示に遷移
                    this.phase = PHASE.RE_OPERATION_CONFIRMATION;
                  } else {
                    // IG ON再操作表示から実行されている場合、条件不成立表示に遷移
                    this.phase = PHASE.CONDITION_FAILED;
                  }
                  break;
                // IgOnStatusがFF, 00以外の場合
                default:
                  // ExitAPIを実行し、結果に関わらず無応答表示に遷移
                  await this.exit();
                  this.phase = PHASE.NON_RESPONSE;
                  break;
              }
              break;
            default:
              switch (response.data.async_code) {
                case error.WSP_GENERAL_ERROR:
                  // ExitAPIを実行し、結果に関わらず否定応答表示に遷移
                  await this.exit();
                  // CGWの場合は'$NRC$', C-ECUの場合は'$**'が文言となるため、両置換を行う
                  this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_101_TXT_7`).replace('$NRC$', '$' + response.data.user_data.nrc).replace('$**', '$' + response.data.user_data.nrc);
                  this.phase = PHASE.NEGATIVE_RESPONSE;
                  break;
                default:
                  // ExitAPIを実行し、結果に関わらず無応答表示に遷移
                  await this.exit();
                  this.phase = PHASE.NON_RESPONSE;
                  break;
              }
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * NonDefaultSession遷移(LAN接続ユニット再登録準備), ルーチン実行(LAN接続ユニット再登録)
     */
    async standby() {
      const token = session.getToken();

      // ルーチン実行(要求)
      const response = await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        this.systemId,
        this.workSupportId,
        {
          id_token: token.id_token,
          type: 'standby'
        },
        // エラー発生のコールバック
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
      );

      if (response) {
        switch (response.data.status) {
          case config.STATUS_WSP_COMPLETED:
            // LAN接続ユニット再登録に遷移
            this.phase = PHASE.RE_REGISTER;
            // 指定時間の計測, 経過後ExitAPI実行
            util.sleep(response.data.user_data.wait_time);
            await this.exit();
            // 結果に関わらず停止に遷移
            this.phase = PHASE.SUCCESS;
            break;
          default:
            switch (response.data.async_code) {
              case error.WSP_GENERAL_ERROR:
                // ExitAPIを実行し、結果に関わらず否定応答表示に遷移
                await this.exit();
                // CGWの場合は'$NRC$', C-ECUの場合は'$**'が文言となるため、両置換を行う
                this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_101_TXT_7`).replace('$NRC$', '$' + response.data.user_data.nrc).replace('**', '$' + response.data.user_data.nrc);
                this.phase = PHASE.NEGATIVE_RESPONSE;
                break;
              default:
                // ExitAPIを実行し、結果に関わらず無応答表示に遷移
                await this.exit();
                this.phase = PHASE.NON_RESPONSE;
                break;
            }
            break;
        }
      }
    },

    /**
     * Terminate  実行のみ行い、結果は返さない
     */
    async exit() {
      const token = session.getToken();
      // Terminate(要求)
      await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        this.systemId,
        this.workSupportId,
        {
          id_token: token.id_token,
          type: 'exit'
        },
        // エラー発生のコールバック
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
      );
    },

    /** 確認表示 OKボタン押下イベント */
    async moveOperationConfirmationEvent() {
      this.phase = PHASE.OPERATION_CONFIRMATION;
    },

    /** IG ON操作表示, IG ON再操作表示 OKボタン押下イベント */
    async readProcessEvent() {
      await this.read();
    },

    /** その他エラーの表示 */
    showUnexpectedError() {
      this.phase = PHASE.NON_RESPONSE;
    },
  },

  async mounted() {
    this.init();
  },
};